import React, { useEffect, useState } from "react";
import {
  Button,
  Divider,
  Select,
  Table,
  Tag,
  Typography,
  Popconfirm,
  App,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { getReadingResources } from "../../../../services/resources/resources.service";
import type { ResourceResponse } from "../../../../types/api/getResources";
import {
  getProgramReadings,
  addResourceToProgram,
  removeResourceFromProgram,
} from "../../../../services/programs/programs.service";
import type { ProgramReadingItem } from "../../../../services/programs/programs.types";
import {
  READING_LEVEL_OPTIONS,
  READING_TYPE_OPTIONS,
} from "../../../../services/reading-content/reading-content.types";

const { Title } = Typography;
const { Option } = Select;

interface ProgramResourceSelectorProps {
  programId: number;
}

const levelLabel = (value: string | null) =>
  READING_LEVEL_OPTIONS.find((o) => o.value === value)?.label ?? value ?? "—";
const typeLabel = (value: string | null) =>
  READING_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value ?? "—";

/**
 * Gestiona las lecturas vinculadas a un programa de Comprensión Lectora.
 * Las altas/bajas pegan directo a los endpoints de ProgramToResource — son
 * independientes del "Guardar" del programa (no viajan en el payload del form).
 */
const ProgramResourceSelector: React.FC<ProgramResourceSelectorProps> = ({
  programId,
}) => {
  const { notification } = App.useApp();
  const [readings, setReadings] = useState<ProgramReadingItem[]>([]);
  const [allReadingResources, setAllReadingResources] = useState<
    ResourceResponse[]
  >([]);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [linked, resources] = await Promise.all([
        getProgramReadings(programId),
        getReadingResources(),
      ]);
      setReadings(linked);
      // El backend ya devuelve solo recursos READING; no se filtra en cliente.
      setAllReadingResources(resources);
    } catch (error) {
      console.error("Error loading program readings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (programId) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const handleAdd = async () => {
    if (!selectedResourceId) return;
    setSaving(true);
    try {
      await addResourceToProgram(programId, selectedResourceId);
      notification.success({
        message: "Lectura agregada",
        placement: "top",
        duration: 2,
      });
      setSelectedResourceId(null);
      await fetchData();
    } catch (error) {
      notification.error({
        message: "No se pudo agregar la lectura",
        placement: "top",
        duration: 3,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (resourceId: number) => {
    setSaving(true);
    try {
      await removeResourceFromProgram(programId, resourceId);
      notification.success({
        message: "Lectura quitada",
        placement: "top",
        duration: 2,
      });
      await fetchData();
    } catch (error) {
      notification.error({
        message: "No se pudo quitar la lectura",
        placement: "top",
        duration: 3,
      });
    } finally {
      setSaving(false);
    }
  };

  // Recursos READING que aún no están vinculados
  const linkedIds = new Set(readings.map((r) => r.resource_id));
  const availableResources = allReadingResources.filter(
    (r) => !linkedIds.has(r.id)
  );

  const columns = [
    {
      title: "Recurso",
      dataIndex: "resource_id",
      key: "resource_id",
      render: (_: unknown, row: ProgramReadingItem) =>
        `${row.resource_id} - ${row.title}`,
    },
    {
      title: "Autor",
      dataIndex: "author",
      key: "author",
      render: (author: string | null) => author ?? "—",
    },
    {
      title: "Nivel",
      dataIndex: "level",
      key: "level",
      render: (level: string | null) =>
        level ? <Tag color="cyan">{levelLabel(level)}</Tag> : "—",
    },
    {
      title: "Género",
      dataIndex: "reading_type",
      key: "reading_type",
      render: (type: string | null) =>
        type ? <Tag color="gold">{typeLabel(type)}</Tag> : "—",
    },
    {
      title: "Quiz",
      dataIndex: "has_game_quiz",
      key: "has_game_quiz",
      render: (hasGameQuiz: boolean) => (hasGameQuiz ? "Sí" : "No"),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, row: ProgramReadingItem) => (
        <Popconfirm
          title="¿Quitar esta lectura del programa?"
          onConfirm={() => handleRemove(row.resource_id)}
          okText="Sí"
          cancelText="No"
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <Divider />
      <Title level={4}>Lecturas del programa</Title>
      <p style={{ color: "#8c8c8c", marginBottom: 16 }}>
        Las lecturas se muestran en la pantalla de Comprensión Lectora,
        filtrables por Nivel y Tipo. El Nivel y el Tipo se configuran en cada
        recurso de lectura.
      </p>

      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Select
          style={{ flex: 1 }}
          placeholder="Selecciona una lectura para agregar"
          value={selectedResourceId ?? undefined}
          onChange={(value) => setSelectedResourceId(value ?? null)}
          showSearch
          filterOption={(input, option) =>
            String(option?.children ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          allowClear
          loading={loading}
        >
          {availableResources.map((r) => (
            <Option key={r.id} value={r.id}>
              {`Recurso ${r.id} - ${r.title}`}
            </Option>
          ))}
        </Select>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={!selectedResourceId}
          loading={saving}
        >
          Agregar lectura
        </Button>
      </div>

      <Table
        rowKey="resource_id"
        columns={columns}
        dataSource={readings}
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText: "Aún no hay lecturas vinculadas a este programa" }}
      />
    </div>
  );
};

export default ProgramResourceSelector;
